import { useState, useEffect } from 'react';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import { formatGenEdCodes } from '@/utils/genEdEntry';

export interface APChoiceOption {
  value: string;
  label: string;
  is_recommended?: boolean;
  gen_ed_codes?: string[];
  recommendation_reason?: string;
}

export interface APChoiceInfo {
  options: APChoiceOption[];
  [key: string]: unknown;
}

export interface APChoiceDialogProps {
  isOpen: boolean;
  choiceInfo: APChoiceInfo | null;
  saving?: boolean;
  onConfirm: (choice: string) => void;
  onCancel: () => void;
}

const APChoiceDialog = ({
  isOpen,
  choiceInfo,
  saving,
  onConfirm,
  onCancel,
}: APChoiceDialogProps) => {
  const [selectedChoice, setSelectedChoice] = useState('');

  useEscapeKey(onCancel, isOpen);

  useEffect(() => {
    if (choiceInfo) {
      const recommended = choiceInfo.options.find((opt) => opt.is_recommended);
      setSelectedChoice(recommended ? recommended.value : choiceInfo.options[0].value);
    }
  }, [choiceInfo]);

  if (!isOpen || !choiceInfo) return null;

  const handleConfirm = (): void => {
    if (!selectedChoice) return;
    onConfirm(selectedChoice);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onCancel} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Course Credit</h3>
        <p className="text-sm text-gray-600 mb-4">
          This AP score can award credit for one of the following courses. Please select which course
          credit you'd like to receive:
        </p>

        <div className="space-y-3 mb-6">
          {choiceInfo.options.map((option) => (
            <label
              key={option.value}
              className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                selectedChoice === option.value
                  ? 'border-primary bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="courseChoice"
                value={option.value}
                checked={selectedChoice === option.value}
                onChange={(e) => setSelectedChoice(e.target.value)}
                className="mt-1 mr-3 text-primary focus:ring-primary"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{option.label}</span>
                  {option.is_recommended && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Recommended
                    </span>
                  )}
                </div>
                {option.gen_ed_codes && option.gen_ed_codes.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    Gen Ed: {formatGenEdCodes(option.gen_ed_codes)}
                  </div>
                )}
                {option.recommendation_reason && (
                  <div className="text-xs text-gray-600 mt-1 italic">
                    {option.recommendation_reason}
                  </div>
                )}
              </div>
            </label>
          ))}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedChoice || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-800 disabled:opacity-50"
          >
            {saving ? 'Adding...' : 'Add Score'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default APChoiceDialog;
